import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  PuzzlePieceIcon,
  UsersIcon,
  CogIcon,
  CreditCardIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  FunnelIcon,
  CpuChipIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Opportunities', href: '/opportunities', icon: MagnifyingGlassIcon },
  { name: 'Watch List', href: '/watchlist', icon: EyeIcon },
  { name: 'Pipeline', href: '/pipeline', icon: FunnelIcon, plan: 'PRO' },
  { name: 'AI Agents', href: '/agents', icon: CpuChipIcon, plan: 'PRO' },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, plan: 'PRO' },
  { name: 'AI Assistant', href: '/ai-assistant', icon: ChatBubbleLeftRightIcon, plan: 'PRO' },
  { name: 'Proposal Writer', href: '/proposal-writer', icon: DocumentTextIcon, plan: 'PRO' },
  { name: 'CRM', href: '/crm', icon: ClipboardDocumentListIcon, plan: 'PRO' },
  { name: 'Documents', href: '/documents', icon: FolderIcon, plan: 'PRO' },
  { name: 'Integrations', href: '/integrations', icon: PuzzlePieceIcon, plan: 'PRO' },
  { name: 'Team', href: '/team', icon: UsersIcon, plan: 'PRO' },
];

const secondaryNavigation = [
  { name: 'Billing', href: '/billing', icon: CreditCardIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userPlan = user?.plan || 'FREE';

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const canAccess = (item: any) => {
    if (!item.plan) return true;
    if (item.plan === 'PRO') return ['PRO', 'ENTERPRISE'].includes(userPlan);
    if (item.plan === 'ENTERPRISE') return userPlan === 'ENTERPRISE';
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white">
            <div className="flex items-center justify-between h-16 px-4 border-b">
              <Link to="/" className="text-xl font-bold gradient-text">Clevor</Link>
              <button onClick={() => setSidebarOpen(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <nav className="px-2 py-4 space-y-1">
              {navigation.filter(canAccess).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop layout */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
          <div className="flex h-16 items-center">
            <Link to="/" className="text-2xl font-bold gradient-text">Clevor</Link>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.filter(canAccess).map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                          isActive(item.href)
                            ? 'bg-brand-50 text-brand-600'
                            : 'text-gray-700 hover:text-brand-600 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="mt-auto">
                <ul role="list" className="-mx-2 space-y-1">
                  {secondaryNavigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                          isActive(item.href)
                            ? 'bg-brand-50 text-brand-600'
                            : 'text-gray-700 hover:text-brand-600 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 justify-end">
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                <BellIcon className="h-6 w-6" />
              </button>

              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

              <div className="flex items-center gap-x-4">
                <span className="hidden lg:flex lg:items-center">
                  <span className="text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">
                    {user?.firstName} {user?.lastName}
                  </span>
                </span>
                <button
                  onClick={() => logout()}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
